import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Heart, ShoppingBag, Menu, X } from "lucide-react";
import { useStore } from "@/context/StoreContext";

const NAV_LINKS = [
  { label: "Acasă", to: "/", anchor: "top" },
  { label: "Parfumuri", to: "/", anchor: "parfumuri" },
  { label: "Despre noi", to: "/", anchor: "despre" },
  { label: "Contact", to: "/", anchor: "contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { cartCount, wishlist, setCartOpen, setSearchOpen, setWishlistOpen } = useStore();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToAnchor = (anchor) => {
    setMobileOpen(false);
    const scroll = () => {
      if (anchor === "top") { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
      const el = document.getElementById(anchor);
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - 72;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    };
    if (location.pathname !== "/") { navigate("/"); setTimeout(scroll, 450); }
    else setTimeout(scroll, 180);
  };

  return (
    <motion.header
      data-testid="main-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-cream/90 backdrop-blur-xl border-b border-line/70 py-3 shadow-[0_8px_30px_rgba(42,36,33,0.05)]" : "bg-transparent py-6"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
        <button onClick={() => goToAnchor("top")} data-testid="brand-logo" className="flex flex-col items-start leading-none group">
          <span className="font-serif text-2xl sm:text-3xl tracking-[0.15em] text-ink font-medium">ORÉLIE</span>
          <span className="eyebrow text-[0.55rem] mt-0.5 text-mocha-dark tracking-[0.35em]">Haute Parfumerie</span>
        </button>

        <div className="hidden lg:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-link-${l.anchor}`}
              onClick={() => goToAnchor(l.anchor)}
              className="relative text-[15px] font-semibold tracking-wide text-ink hover:text-mocha-dark transition-colors duration-300 group"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-mocha-dark transition-all duration-500 group-hover:w-full" />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-4 sm:gap-5">
          <button data-testid="search-toggle-btn" onClick={() => setSearchOpen(true)} aria-label="Căutare" className="text-ink hover:text-mocha-dark transition-colors">
            <Search size={20} strokeWidth={1.5} />
          </button>
          <button data-testid="wishlist-toggle-btn" onClick={() => setWishlistOpen(true)} aria-label="Favorite" className="relative text-ink hover:text-mocha-dark transition-colors">
            <Heart size={20} strokeWidth={1.5} />
            {wishlist.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#C1362F] text-cream text-[10px] w-4 h-4 flex items-center justify-center rounded-full">{wishlist.length}</span>
            )}
          </button>
          <button data-testid="cart-toggle-btn" onClick={() => setCartOpen(true)} aria-label="Coș" className="relative text-ink hover:text-mocha-dark transition-colors">
            <ShoppingBag size={20} strokeWidth={1.5} />
            {cartCount > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-2 -right-2 bg-ink text-cream text-[10px] w-4 h-4 flex items-center justify-center rounded-full">{cartCount}</span>
            )}
          </button>
          <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen((v) => !v)} className="lg:hidden text-ink">
            {mobileOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-cream/95 backdrop-blur-xl border-t border-line/60 mt-3"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <button key={l.label} onClick={() => goToAnchor(l.anchor)} className="text-left font-serif text-2xl text-ink">{l.label}</button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
